import type { TenantBranding } from '@an-crm/shared';

const HEX = /^#([0-9a-f]{6})$/i;

function toRgb(hex: string): [number, number, number] | null {
  const m = HEX.exec(hex);
  if (!m?.[1]) return null;
  const n = Number.parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function channel(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance of a #RRGGBB colour, or null if invalid. */
export function relativeLuminance(hex: string): number | null {
  const rgb = toRgb(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb;
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrastRatio(a: string, b: string): number | null {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la === null || lb === null) return null;
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Approximates the app's `color-mix(in oklab, <hex> <pct>%, black)` solid shade in sRGB, close enough
 * to judge text contrast on primary buttons the way they are actually rendered.
 */
export function mixWithBlack(hex: string, pct: number): string {
  const rgb = toRgb(hex);
  if (!rgb) return hex;
  return `#${rgb
    .map((c) =>
      Math.round(c * (pct / 100))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

/** Text colour (white or charcoal) with the better contrast on the given background. */
export function readableOn(hex: string): '#ffffff' | '#2b323b' {
  const white = contrastRatio(hex, '#ffffff') ?? 21;
  const dark = contrastRatio(hex, '#2b323b') ?? 1;
  return white >= dark ? '#ffffff' : '#2b323b';
}

const OVERRIDDEN = ['--brand-primary', '--brand-accent', '--brand-primary-fg'] as const;

/**
 * Applies tenant colours over the Ankyah defaults. Very light primaries are rejected (they would
 * make the derived text/solid colours illegible); derived tokens are computed in CSS via color-mix.
 */
export function applyBranding(
  branding: TenantBranding | null | undefined,
  root: HTMLElement = document.documentElement,
): void {
  for (const prop of OVERRIDDEN) root.style.removeProperty(prop);
  if (!branding) return;
  const primaryLum = branding.primaryColor ? relativeLuminance(branding.primaryColor) : null;
  if (branding.primaryColor && primaryLum !== null && primaryLum < 0.7) {
    root.style.setProperty('--brand-primary', branding.primaryColor);
    // the solid fill is ~82% of the primary mixed with black, so judge readability on a darker shade
    root.style.setProperty('--brand-primary-fg', primaryLum > 0.45 ? '#2b323b' : '#ffffff');
  }
  if (branding.accentColor && relativeLuminance(branding.accentColor) !== null) {
    root.style.setProperty('--brand-accent', branding.accentColor);
  }
}

export function resetBranding(root?: HTMLElement): void {
  applyBranding(null, root);
}
